import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Tags } from 'components/tags';
import { FovSelect, FovOption, FovToggle, FovDivider, VerticalSpacer } from 'components';
import { ProtectedContent } from 'components/generic/ProtectedContent';
import { Proxies } from 'store/actions/rpc';
import {
  PublishFormatsSelect,
  PUBLISH_FORMATS,
  DEFAULT_PUBLISH_FORMATS,
} from '../../ModelCanvas/SideMenus/RPCForm/PublishFormatsField';
import {
  SidebarPanel,
  SidebarContent,
  SidebarTitle,
  SidebarSection,
  SectionHeader,
  SectionLabel,
  ModelSelectWrapper,
  ModelPickerRow,
  ModelPickerSelect,
  ModelDropdown,
  ModelDropdownArrow,
  ModelPickerSettingsButton,
  GenerateButton,
  GenerateButtonSpinner,
  GenerateCaption,
  SidebarHelperText,
  SimpleFieldLabel,
  SimpleFieldInput,
  SimpleTagsBox,
  SimpleToggleRow,
} from '../imageCanvasStyles';
import { TENCENT_DEFAULTS } from '../tencentSettings';
import { TencentSettingsModal } from './TencentSettingsModal';
import { MESHY_DEFAULTS } from '../meshySettings';
import { MeshySettingsModal } from './MeshySettingsModal';
import { TRIPO_DEFAULTS } from '../tripoSettings';
import { TripoSettingsModal } from './TripoSettingsModal';
import { RODIN_DEFAULTS } from '../rodinSettings';
import { RodinSettingsModal } from './RodinSettingsModal';
import _ from 'lodash';

const AI_MODELS = [
  { value: 'meshy', label: 'Meshy AI' },
  { value: 'tripo', label: 'Tripo AI' },
  { value: 'rodin', label: 'Rodin AI' },
  { value: 'tencent', label: 'Tencent AI' },
];

const proxyOptions = [
  Proxies.billboard,
  Proxies.simpleMesh,
  Proxies.boundingBox,
];

const buildCategoriesOptions = rpcCategories => {
  if (!_.isEmpty(rpcCategories)) {
    return rpcCategories.map(category => {
      const hasChildren = category.children?.length > 0;
      const childrenArray = hasChildren && [ ...category.children, category ];
      const arrayWithNested = hasChildren ? childrenArray.reverse() : [ category ];

      return arrayWithNested.map((item, index) => {
        const { title, value, children } = item;

        return (
          <FovOption
            key={index}
            value={value}
            indent={!children}
            groupParent={children}
          >
            {title}
          </FovOption>
        );
      });
    });
  }

  return (
    <FovOption value='No categories found.'>
      No categories found.
    </FovOption>
  );
};

const SimpleModePanel = ({
  theme,
  selectedTheme,
  hasImage,
  imageName,
  provider,
  onProviderChange,
  isGenerating,
  onGenerate,
}) => {
  const shared = { theme, $selectedTheme: selectedTheme };
  const rpcCategories = useSelector(state => state.rpc.rpcCategories);
  const categoryOptions = useMemo(() => buildCategoriesOptions(rpcCategories), [ rpcCategories ]);
  const [ name, setName ] = useState('');
  const [ category, setCategory ] = useState('Other');
  const [ tags, setTags ] = useState('Meshy');
  const [ tagsManuallyEdited, setTagsManuallyEdited ] = useState(false);
  const [ proxy, setProxy ] = useState(Proxies.billboard);
  const [ formats, setFormats ] = useState(DEFAULT_PUBLISH_FORMATS);
  const [ debugState, setDebugState ] = useState(false);
  const [ tencentSettings, setTencentSettings ] = useState(TENCENT_DEFAULTS);
  const [ tencentSettingsOpen, setTencentSettingsOpen ] = useState(false);
  const [ meshySettings, setMeshySettings ] = useState(MESHY_DEFAULTS);
  const [ meshySettingsOpen, setMeshySettingsOpen ] = useState(false);
  const [ tripoSettings, setTripoSettings ] = useState(TRIPO_DEFAULTS);
  const [ tripoSettingsOpen, setTripoSettingsOpen ] = useState(false);
  const [ rodinSettings, setRodinSettings ] = useState(RODIN_DEFAULTS);
  const [ rodinSettingsOpen, setRodinSettingsOpen ] = useState(false);

  useEffect(() => {
    if (imageName && !name) {
      setName(imageName.replace(/\.[^.]+$/, ''));
    }
  }, [ imageName ]);

  useEffect(() => {
    if (!tagsManuallyEdited) {
      setTags(provider.charAt(0).toUpperCase() + provider.slice(1));
    }
  }, [ provider ]);

  const generateDisabled = !hasImage || isGenerating;
  const idleCaption = hasImage
    ? 'Convert your image into a 3D model'
    : 'Upload an image first to generate a 3D model.';
  const generateCaption = isGenerating
    ? 'Packaging and uploading… your model will appear in your library when it is ready.'
    : idleCaption;

  return (
    <SidebarPanel {...shared}>
      <SidebarContent>
        <SidebarTitle {...shared}>Publish</SidebarTitle>

        {!hasImage && (
          <SidebarHelperText {...shared}>
            Upload an image to generate a 3D model.
          </SidebarHelperText>
        )}

        <SidebarSection>
          <SectionHeader>
            <SectionLabel {...shared}>AI Provider</SectionLabel>
          </SectionHeader>

          <ModelPickerRow>
            <ModelPickerSelect>
              <ModelSelectWrapper>
                <ModelDropdown
                  {...shared}
                  value={provider}
                  onChange={e => onProviderChange(e.target.value)}
                  aria-label='AI provider'
                >
                  {AI_MODELS.map(({ value, label }) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </ModelDropdown>
                <ModelDropdownArrow {...shared}>&#9660;</ModelDropdownArrow>
              </ModelSelectWrapper>
            </ModelPickerSelect>

            {provider === 'tencent' && (
              <ModelPickerSettingsButton
                {...shared}
                type='button'
                aria-label='Tencent AI settings'
                title='Settings'
                onClick={() => setTencentSettingsOpen(true)}
              >
                &#9881;
              </ModelPickerSettingsButton>
            )}

            {provider === 'meshy' && (
              <ModelPickerSettingsButton
                {...shared}
                type='button'
                aria-label='Meshy AI settings'
                title='Settings'
                onClick={() => setMeshySettingsOpen(true)}
              >
                &#9881;
              </ModelPickerSettingsButton>
            )}

            {provider === 'tripo' && (
              <ModelPickerSettingsButton
                {...shared}
                type='button'
                aria-label='Tripo AI settings'
                title='Settings'
                onClick={() => setTripoSettingsOpen(true)}
              >
                &#9881;
              </ModelPickerSettingsButton>
            )}

            {provider === 'rodin' && (
              <ModelPickerSettingsButton
                {...shared}
                type='button'
                aria-label='Rodin AI settings'
                title='Settings'
                onClick={() => setRodinSettingsOpen(true)}
              >
                &#9881;
              </ModelPickerSettingsButton>
            )}
          </ModelPickerRow>

          <PublishFormatsSelect
            compact
            options={PUBLISH_FORMATS}
            value={formats}
            onChange={setFormats}
          />
        </SidebarSection>

        <SidebarSection>
          <SimpleFieldLabel {...shared}>Name</SimpleFieldLabel>
          <SimpleFieldInput
            {...shared}
            type='text'
            value={name}
            placeholder='Model name'
            onChange={e => setName(e.target.value)}
          />

          <SimpleFieldLabel {...shared}>Category</SimpleFieldLabel>
          <ModelSelectWrapper>
            <FovSelect
              value={category}
              placeholder={category}
              disabled={_.isEmpty(rpcCategories)}
              onChange={(e, newVal) => setCategory(newVal)}
            >
              {categoryOptions}
            </FovSelect>
          </ModelSelectWrapper>

          <SimpleFieldLabel {...shared}>Tags</SimpleFieldLabel>
          <SimpleTagsBox {...shared}>
            <Tags
              values={tags}
              newTagText='Add tag'
              onChange={(e, value) => {
                setTags(value);
                setTagsManuallyEdited(true);
              }}
            />
          </SimpleTagsBox>

          <SimpleFieldLabel {...shared}>Proxy</SimpleFieldLabel>
          <ModelSelectWrapper>
            <FovSelect
              value={proxy}
              placeholder={proxy}
              onChange={(e, newVal) => setProxy(newVal)}
            >
              {proxyOptions.map((option, index) => (
                <FovOption key={index} value={option}>
                  {option}
                </FovOption>
              ))}
            </FovSelect>
          </ModelSelectWrapper>
        </SidebarSection>

        <ProtectedContent>
          <FovDivider />
          <VerticalSpacer size={15} />

          <SimpleToggleRow {...shared}>
            Debug Package

            <FovToggle
              name='debugToggle'
              checked={!!debugState}
              callback={() => setDebugState(!debugState)}
            />
          </SimpleToggleRow>

          <VerticalSpacer size={15} />
        </ProtectedContent>

        <SidebarSection>
          <GenerateButton
            {...shared}
            disabled={generateDisabled}
            onClick={() => onGenerate({
              name,
              category,
              tags,
              proxy,
              formats,
              debug: debugState,
              tencentSettings: provider === 'tencent' ? tencentSettings : undefined,
              meshySettings: provider === 'meshy' ? meshySettings : undefined,
              tripoSettings: provider === 'tripo' ? tripoSettings : undefined,
              rodinSettings: provider === 'rodin' ? rodinSettings : undefined,
            })}
          >
            {isGenerating
              ? (<><GenerateButtonSpinner />Generating 3D model...</>)
              : (<><span aria-hidden='true'>&#9678;</span> Publish</>)}
          </GenerateButton>
          <GenerateCaption {...shared}>
            {generateCaption}
          </GenerateCaption>
        </SidebarSection>
      </SidebarContent>

      <TencentSettingsModal
        theme={theme}
        selectedTheme={selectedTheme}
        visible={tencentSettingsOpen}
        settings={tencentSettings}
        onCancel={() => setTencentSettingsOpen(false)}
        onSave={newSettings => {
          setTencentSettings(newSettings);
          setTencentSettingsOpen(false);
        }}
      />

      <MeshySettingsModal
        theme={theme}
        selectedTheme={selectedTheme}
        visible={meshySettingsOpen}
        settings={meshySettings}
        onCancel={() => setMeshySettingsOpen(false)}
        onSave={newSettings => {
          setMeshySettings(newSettings);
          setMeshySettingsOpen(false);
        }}
      />

      <TripoSettingsModal
        theme={theme}
        selectedTheme={selectedTheme}
        visible={tripoSettingsOpen}
        settings={tripoSettings}
        onCancel={() => setTripoSettingsOpen(false)}
        onSave={newSettings => {
          setTripoSettings(newSettings);
          setTripoSettingsOpen(false);
        }}
      />

      <RodinSettingsModal
        theme={theme}
        selectedTheme={selectedTheme}
        visible={rodinSettingsOpen}
        settings={rodinSettings}
        onCancel={() => setRodinSettingsOpen(false)}
        onSave={newSettings => {
          setRodinSettings(newSettings);
          setRodinSettingsOpen(false);
        }}
      />
    </SidebarPanel>
  );
};

export { SimpleModePanel };
